// Energy Mix Donut Chart
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { EnergyBalanceSummary } from '@/lib/epseEnergyBalance';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';

interface EPSEEnergyMixDonutProps {
  summary: EnergyBalanceSummary;
  title?: string;
}

export function EPSEEnergyMixDonut({ summary, title = "Energiemix" }: EPSEEnergyMixDonutProps) {
  const totalSupplied = summary.actual_grid_kwh + summary.pv_used_kwh + summary.battery_supplied_kwh;
  
  const data = [
    {
      name: 'Net Inkoop',
      value: summary.actual_grid_kwh,
      color: 'hsl(var(--chart-1))',
      percent: totalSupplied > 0 ? (summary.actual_grid_kwh / totalSupplied) * 100 : 0,
    },
    {
      name: 'Directe Zon',
      value: summary.pv_used_kwh,
      color: 'hsl(142 76% 36%)',
      percent: totalSupplied > 0 ? (summary.pv_used_kwh / totalSupplied) * 100 : 0,
    },
    {
      name: 'Uit Batterij',
      value: summary.battery_supplied_kwh,
      color: 'hsl(var(--chart-3))',
      percent: totalSupplied > 0 ? (summary.battery_supplied_kwh / totalSupplied) * 100 : 0,
    },
  ].filter(d => d.value > 0);

  // Add unmet demand if present
  if (summary.unmet_demand_kwh > 0) {
    data.push({
      name: 'Tekort',
      value: summary.unmet_demand_kwh,
      color: 'hsl(var(--destructive))',
      percent: (summary.unmet_demand_kwh / summary.total_demand_kwh) * 100,
    });
  }

  const totalKwh = summary.total_demand_kwh;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-[250px] relative">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={90}
                paddingAngle={2}
                dataKey="value"
                label={({ name, percent }) => `${name}: ${percent.toFixed(0)}%`}
                labelLine={false}
              >
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip
                formatter={(value: number, name: string) => [
                  `${value.toFixed(1)} kWh (${((value / totalKwh) * 100).toFixed(1)}%)`,
                  name,
                ]}
                contentStyle={{
                  backgroundColor: 'hsl(var(--card))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '8px',
                }}
              />
            </PieChart>
          </ResponsiveContainer>
          
          {/* Center text */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="text-center">
              <p className="text-2xl font-bold">{(totalKwh / 1000).toFixed(1)}</p>
              <p className="text-xs text-muted-foreground">MWh/dag</p>
            </div>
          </div>
        </div>
        
        {/* Legend with values */}
        <div className="grid grid-cols-2 gap-2 mt-4 text-sm">
          {data.map((entry) => (
            <div key={entry.name} className="flex items-center gap-2">
              <div 
                className="w-3 h-3 rounded-full" 
                style={{ backgroundColor: entry.color }} 
              />
              <span className="text-muted-foreground">{entry.name}:</span>
              <span className="font-medium">{entry.value.toFixed(0)} kWh</span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
